<template>
  <div>
      <v-dialog v-model="showWelcomeDialog" contained max-width="800" :persistent="!dataLoaded" class="custom-dialog welcome-dialog">
          <v-card rounded="lg" class="overflow-hidden welcome-card">

              <!-- Hero: background image with fonted welcome message -->
              <div class="welcome-hero">
                  <v-progress-circular v-if="!projectName"
                                       indeterminate
                                       color="white"
                                       size="x-large"
                                       class="mb-4" />
                  <div v-else
                       ref="titleEl"
                       class="welcome-hero-title"
                       @dblclick="copyQueryString">
                      {{ $t('welcomeMessage', { projectName: projectName }) }}
                  </div>

                  <v-btn v-if="projectName"
                         class="hero-btn text-uppercase"
                         color="#2f9bfe"
                         rounded="pill"
                         size="x-large"
                         variant="flat"
                         :loading="loadingData"
                         :disabled="loadingData || !isDuckDBInitialized"
                         @click="loadDashboard">
                      {{ $t('loadDashboard') }}
                  </v-btn>

                  <!-- Zero-height wrapper: the spinner overflows into the space below the
                       button without shifting the centered title/button at all -->
                  <div v-if="projectName" class="hero-init-spinner">
                      <v-progress-circular v-if="!isDuckDBInitialized && !loadingData"
                                           indeterminate
                                           color="white"
                                           size="24"
                                           width="2"
                                           class="mt-3" />
                  </div>

                  <!-- Overall progress, overlaid on the bottom of the hero image -->
                  <div v-if="!projectName || loadingData" class="hero-progress">
                      <v-progress-linear :model-value="progressValue"
                                         color="#2f9bfe"
                                         height="6"
                                         class="mb-1"
                                         rounded />
                      <div class="text-caption text-center hero-progress-text text-truncate">
                          {{ apiProgress }} / {{ totalApiRequests }} {{ $t('loaded') }}
                      </div>
                  </div>
              </div>

          </v-card>
      </v-dialog>

      <!-- Toast Message -->
      <v-snackbar v-model="showToast"
                  :timeout="2000"
                  color="success"
                  location="top">
          {{ $t('devModeActivated') }}
      </v-snackbar>

      <!-- Error Toast for Copy Failure -->
      <v-snackbar v-model="showErrorToast"
                  :timeout="3000"
                  color="error"
                  location="top">
          {{ $t('copyFailed') }}
      </v-snackbar>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useAnalytics } from '@/utils/useAnalytics'

const store = useStore();

// Shrink the title font just enough to keep the whole message on one line.
// The title keeps its CSS clamp() size as the maximum; only longer names scale down.
const titleEl = ref(null);
let titleResizeObserver = null;

const fitTitle = () => {
  const el = titleEl.value;
  if (!el) return;
  el.style.fontSize = '';
  if (el.scrollWidth <= el.clientWidth) return;
  const base = parseFloat(window.getComputedStyle(el).fontSize);
  // 0.995: scrollWidth/clientWidth are rounded to integers, leave sub-pixel slack
  el.style.fontSize = (base * (el.clientWidth / el.scrollWidth) * 0.995) + 'px';
};

// The title element mounts late (dialog opens, then projectName arrives), so react to
// the ref itself rather than fitting once on mount
watch(titleEl, (el) => {
  if (titleResizeObserver) {
    titleResizeObserver.disconnect();
    titleResizeObserver = null;
  }
  if (el) {
    titleResizeObserver = new ResizeObserver(fitTitle);
    titleResizeObserver.observe(el.parentElement);
    fitTitle();
    // The Adobe font loads async and is wider/narrower than the fallback — re-measure
    document.fonts?.ready?.then(fitTitle);
  }
});

watch(() => store.getters.getProjectName, () => nextTick(fitTitle));

onBeforeUnmount(() => {
  if (titleResizeObserver) titleResizeObserver.disconnect();
});

const { trackEvent } = useAnalytics()

const showWelcomeDialog = computed(() => store.getters.getShowWelcomeDialog);
const isDuckDBInitialized = computed(() => store.getters.isDuckDBInitialized);
const showToast = ref(false);
const showErrorToast = ref(false);

const projectName = computed(() => store.getters.getProjectName);
const loadingData = ref(false);
const dataLoaded = computed(() => store.getters.getDataLoaded);
const queryString = computed(() => window.location.search);

// Props for progress tracking
const props = defineProps({
apiProgress: {
  type: Number,
  required: true
},
totalApiRequests: {
  type: Number,
  required: true
}
});

// Compute the progress percentage
const progressValue = computed(() => {
return (props.apiProgress / props.totalApiRequests) * 100;
});

// Requires the embedding SPA to delegate clipboard access on the iframe tag
// (<iframe allow="clipboard-write">) — without it, cross-origin iframes are
// denied by permissions policy and writeText rejects.
const copyQueryString = () => {
  trackEvent('copyQueryString', {
    action_type: 'avatar_double_click',
    project_code: store.getters.getProjectCode,
    project_name: store.getters.getProjectName
  });

  navigator.clipboard.writeText(queryString.value)
    .then(() => { showToast.value = true; })
    .catch((err) => {
      console.error('Copy failed:', err);
      showErrorToast.value = true;
    });
};

const loadDashboard = async () => {
trackEvent('loadDashboard', {
  action_type: 'button_click',
  project_code: store.getters.getProjectCode,
  project_name: store.getters.getProjectName
})

loadingData.value = true;
await store.dispatch('updateLoadDashboard', true);
};

watch(dataLoaded, (newValue) => {
if (newValue) {
  store.dispatch('updateShowWelcomeDialog', false);
}
});

</script>

<style>
  /* "sofia-pro-variable" is served by Adobe Fonts — see the use.typekit.net link in index.html */
  .welcome-dialog .v-overlay__content {
      font-family: 'sofia-pro-variable', 'Helvetica Neue', Arial, sans-serif;
  }

  /* Vuetify's typography utilities (text-body-1, text-caption, ...) hardcode Roboto with
     !important — force them back to the dialog font. */
  .welcome-dialog .v-overlay__content [class*='text-'] {
      font-family: inherit !important;
  }

  .custom-dialog .v-overlay__content {
      top: 20vh !important;
  }

  /* Keep the welcome dialog centered in the viewport (cancels .custom-dialog's top offset).
     align-self: center stops the overlay from stretching the content to full container height. */
  .welcome-dialog .v-overlay__content {
      max-height: none !important;
      top: auto !important;
      align-self: center;
  }

  /* Vuetify flexes dialog cards to fill the overlay content, which silently overrides the
     card's own height — opt out so the hero's aspect-ratio height applies. The explicit
     width is required for Safari, which otherwise sizes the card from its content instead
     of the dialog's max-width. */
  .welcome-dialog .v-overlay__content > .v-card.welcome-card {
      flex: 0 0 auto;
      width: 100%;
      max-width: 100%;
  }

  .welcome-hero {
      background: url('../../../assets/background.png') center / cover no-repeat;
      /* Match the background image's native proportions (1500x582) so it's never cropped */
      aspect-ratio: 1500 / 582;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 24px;
      position: relative;
      flex-shrink: 0;
  }

  .hero-progress {
      position: absolute;
      left: 24px;
      right: 24px;
      bottom: 12px;
  }

  .hero-progress-text {
      color: rgba(255, 255, 255, 0.85);
  }

  .welcome-hero-title {
      color: #ffffff;
      font-size: clamp(2.25rem, 6vw, 3.25rem);
      font-weight: 700;
      line-height: 1.15;
      letter-spacing: 0.01em;
      margin-bottom: 36px;
      user-select: none;
      /* Single line always — fitTitle() shrinks the font to fit instead of wrapping.
         max-width caps the element at the hero's content width so overflow is measurable
         (the hero centers children at their intrinsic width otherwise). */
      white-space: nowrap;
      max-width: 100%;
  }

  .hero-init-spinner {
      height: 0;
      overflow: visible;
      display: flex;
      align-items: flex-start;
      justify-content: center;
  }

  .hero-btn {
      min-width: 300px;
      /* !important needed — Vuetify's button classes set font-size/weight at equal specificity */
      font-size: 0.95rem !important;
      font-weight: 900 !important;
      letter-spacing: 0.06em;
      color: #ffffff !important;
  }
</style>
